import type { SxProps, Theme } from '@mui/material/styles'

import { typography } from '../KestrixDesignSystem.jsx'
import { viewTabTokens as tokens } from './view-tab-tokens.ts'
import type { ViewTabVariant } from './ViewTab.types.ts'

type ViewTabVariantColors = {
  background: string
  border: string
  text: string
  /** Omitted when the variant keeps its resting background on hover. */
  hoverBackground?: string
  /** Omitted when the variant keeps its resting background while pressed. */
  pressBackground?: string
}

/**
 * Single source of truth for per-variant colour. Keyed by `ViewTabVariant`, so
 * adding a variant is a compile error until its palette is declared here.
 */
const VARIANT_COLORS: Record<ViewTabVariant, ViewTabVariantColors> = {
  'not-selected': tokens.colors.notSelected,
  active: tokens.colors.active,
  modified: tokens.colors.modified,
  'new-view': tokens.colors.newView
}

/** Whether a variant renders as the currently active tab (KDS active/modified). */
export const isActiveVariant = (variant: ViewTabVariant): boolean =>
  variant === 'active' || variant === 'modified'

/**
 * Paint the modified palette while a name or description draft is unsaved.
 * New View stays an action control and never takes that palette.
 */
export const viewTabVisualVariant = (
  variant: ViewTabVariant,
  unsaved: boolean
): ViewTabVariant => {
  if (unsaved && variant !== 'new-view') {
    return 'modified'
  }
  return variant
}

export const getVariantColors = (
  variant: ViewTabVariant
): ViewTabVariantColors => VARIANT_COLORS[variant]

export const getTabStyles = (
  variant: ViewTabVariant,
  mounted: boolean
): SxProps<Theme> => {
  const colors = getVariantColors(variant)
  const stateTransition = mounted ? tokens.transitions.state : 'none'
  const pointerTransition = mounted ? tokens.transitions.pointer : 'none'

  const topRadius = isActiveVariant(variant)
    ? tokens.sizing.activeTabTopRadius
    : tokens.sizing.tabTopRadius

  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.sizing.gap,
    p: tokens.sizing.padding,
    minHeight: tokens.sizing.minHeight,
    height: tokens.sizing.minHeight,
    boxSizing: 'border-box',
    border: `1px solid ${colors.border}`,
    ...(isActiveVariant(variant) && {
      borderBottomColor: 'transparent'
    }),
    borderRadius: `${topRadius} ${topRadius} 0 0`,
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.body.small.fontSize,
    fontWeight: isActiveVariant(variant)
      ? typography.fontWeight.medium
      : typography.body.small.fontWeight,
    lineHeight: typography.body.small.lineHeight,
    cursor: 'pointer',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    boxShadow: 'none',
    transition: `${stateTransition}, ${pointerTransition}`,
    ...(colors.hoverBackground != null && {
      '&:hover': {
        backgroundColor: colors.hoverBackground
      }
    }),
    ...(colors.pressBackground != null && {
      '&:active': {
        backgroundColor: colors.pressBackground
      }
    }),
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `2px solid ${tokens.colors.focusRing}`,
      outlineOffset: 2
    },
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
      pointerEvents: 'auto'
    }
  }
}
